import { useMemo } from 'react';

import { useGetRecentSubsets } from '@/api/queries/useGetRecentSubsets';
import { Subset } from '@/api/types/backend-dto-types';
import { BreadcrumbItem } from '@/new/components/breadcrumb';
import { SubsetBreadcrumbDropdown, useMatterBreadcrumbHead } from '@/new/components/breadcrumb-dropdown';

interface MatterLike {
  name: string;
  allDocumentsCaseId?: string | null;
}

interface UseSubsetBreadcrumbsOptions {
  matterId: string;
  subsetId: string;
  matter: MatterLike | undefined;
  subset: Subset | undefined;
  /**
   * The current page information. If null, indicates this is the subset overview page.
   */
  currentPage: {
    label: string;
    path: string;
  } | null;
}

/**
 * Generates breadcrumb items for subset-level pages.
 * When viewing the "All Documents" subset, it omits the Subsets and subset name items
 * to make it appear as if the user is in the Matter context.
 */
export function useSubsetBreadcrumbs({
  matterId,
  subsetId,
  matter,
  subset,
  currentPage,
}: UseSubsetBreadcrumbsOptions): BreadcrumbItem[] {
  const isAllDocumentsSubset = subset?.isAllDocuments ?? (matter ? matter.allDocumentsCaseId === subsetId : undefined);

  const head = useMatterBreadcrumbHead(matterId, matter?.name, isAllDocumentsSubset === true && !currentPage);

  // Fall back to the recent-subsets cache for the name while the subset query loads.
  const { data: recentSubsets } = useGetRecentSubsets(matterId);
  const subsetName = subset?.name ?? recentSubsets?.find((s) => s.caseId === subsetId)?.case.name;

  return useMemo(() => {
    const items: BreadcrumbItem[] = [...head];

    // The matter is still loading; `Breadcrumb` renders the trailing skeleton and stops.
    if (items.some((item) => item.loading)) {
      return items;
    }

    // For All Documents, skip the Subsets/subset items so it reads as the matter context.
    if (isAllDocumentsSubset === false) {
      items.push({ href: `/matters/${matterId}/subsets`, label: 'Subsets', active: false });
      items.push(
        subsetName !== undefined
          ? {
              key: 'subset',
              render: (
                <SubsetBreadcrumbDropdown
                  matterId={matterId}
                  subsetId={subsetId}
                  subsetName={subsetName}
                  active={!currentPage}
                />
              ),
            }
          : { key: 'subset', loading: true },
      );
    }

    if (currentPage) {
      items.push({
        href: `/matters/${matterId}/subsets/${subsetId}/${currentPage.path}`,
        label: currentPage.label,
        active: true,
      });
    }

    return items;
  }, [head, isAllDocumentsSubset, subsetName, matterId, subsetId, currentPage]);
}
